import {
  isPfpWorksiteRoom,
  useRoomGetRoom,
  useRoomRoomCheckIn,
  useRoomTimeEntriesScan,
} from "@crowdcoursing/api/openapi";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useFeature } from "~hooks/useFeature";
import { useToast } from "~hooks/useToast";
import { Route } from "~services/router/Route";
import { CheckInResult, type CheckInResultVariant } from "~ui/molecules/CheckIn/CheckInResult";
import { valueOrFallback } from "~utils/sanitizeDto";

const tCheckingIn = "Checking in...";
const tLegacyCheckInSuccess = "You have been checked in";
const tLegacyCheckInError = "Failed to check in. Please try again.";
const tWorksiteCheckInSuccess = "Checked in";
const tWorksiteCheckOutSuccess = "Checked out";
const tWorksiteScanError = "Could not record your time entry. Please try again.";
const tRoomLoadError = "Could not load this room.";
const tGoToRoom = "Go to room";
const tGoHome = "Go home";

function formatTime(value?: string | null) {
  if (!value) {
    return "—";
  }

  return Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatDuration(totalMinutes?: number) {
  if (totalMinutes == null) {
    return "";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h`;
  }
  return `${minutes}m`;
}

export function RoomCheckInPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const apprenticeshipsEnabled = useFeature("apprenticeships");
  const { roomId = "" } = useParams<{ roomId: string }>();
  const hasScannedRef = useRef(false);

  const [variant, setVariant] = useState<CheckInResultVariant>("loading");
  const [title, setTitle] = useState(tCheckingIn);
  const [message, setMessage] = useState<string | undefined>();

  const {
    data: room,
    isError: isRoomError,
    isSuccess: isRoomLoaded,
  } = useRoomGetRoom({ pathParams: { roomId } }, { enabled: Boolean(roomId) });

  const { mutate: legacyCheckIn } = useRoomRoomCheckIn({
    onError() {
      setVariant("error");
      setTitle(tLegacyCheckInError);
      setMessage(undefined);
      toast.error(tLegacyCheckInError);
    },
    onSuccess() {
      setVariant("success");
      setTitle(tLegacyCheckInSuccess);
      setMessage(undefined);
      toast.success(tLegacyCheckInSuccess);
    },
  });

  const { mutate: scanWorksite } = useRoomTimeEntriesScan({
    onError() {
      setVariant("error");
      setTitle(tWorksiteScanError);
      setMessage(undefined);
      toast.error(tWorksiteScanError);
    },
    onSuccess(entry) {
      const checkedOut = Boolean(entry.outAt);
      if (checkedOut) {
        const duration = formatDuration(entry.totalMinutes);
        setVariant("checkout");
        setTitle(tWorksiteCheckOutSuccess);
        setMessage(
          duration
            ? `Out at ${formatTime(entry.outAt)}. Total time: ${duration}.`
            : `Out at ${formatTime(entry.outAt)}.`,
        );
        toast.success(tWorksiteCheckOutSuccess);
        return;
      }

      setVariant("success");
      setTitle(tWorksiteCheckInSuccess);
      setMessage(`In at ${formatTime(entry.inAt)}.`);
      toast.success(tWorksiteCheckInSuccess);
    },
  });

  useEffect(() => {
    if (!roomId || hasScannedRef.current) {
      return;
    }

    if (isRoomError) {
      setVariant("error");
      setTitle(tRoomLoadError);
      return;
    }

    if (!isRoomLoaded || !room) {
      return;
    }

    hasScannedRef.current = true;

    const pfpWorksite = apprenticeshipsEnabled && isPfpWorksiteRoom(room);
    if (pfpWorksite) {
      scanWorksite({
        body: { source: 0 },
        pathParams: { roomId },
      });
      return;
    }

    legacyCheckIn({
      pathParams: {
        roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "(RoomCheckInPage) Missing roomId in url params"),
      },
    });
  }, [apprenticeshipsEnabled, isRoomError, isRoomLoaded, legacyCheckIn, room, roomId, scanWorksite]);

  const handleGoToRoom = () => {
    navigate(
      Route.roomView({
        roomId: valueOrFallback(roomId, "MISSING_ROOM_ID", "(RoomCheckInPage) Missing roomId in url params"),
      }),
    );
  };

  const actionLabel = variant === "error" ? tGoHome : tGoToRoom;
  const onAction = variant === "error" ? () => navigate(Route.home()) : handleGoToRoom;

  return (
    <CheckInResult
      actionLabel={variant === "loading" ? undefined : actionLabel}
      message={message}
      title={title}
      variant={variant}
      onAction={variant === "loading" ? undefined : onAction}
    />
  );
}
